// src/ui/screens/chats/ForwardContactSelector.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { UserAvatar } from '../../../components/UserAvatar';
import { chatService } from '../../../services/chat/chatService';
import { messageActionsService } from '../../../services/chat/messageActionsService';
import { userProfileService } from '../../../services/user/userProfileService';
import { db } from '../../../firebase';
import { collection, getDocs } from 'firebase/firestore';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
};

// ── Forward Target ──────────────────────────────────────────────────────────

class ForwardTarget {
  constructor({ uid, name, avatarUrl = null }) {
    this.uid = uid;
    this.name = name;
    this.avatarUrl = avatarUrl;
  }
}

// ── Contact Tile ────────────────────────────────────────────────────────────

const ContactTile = ({ target, selected, onTap }) => {
  return (
    <button
      onClick={onTap}
      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-filatom-card-hover transition-colors"
    >
      <div className="relative flex-shrink-0">
        <UserAvatar
          size={46}
          imageUrl={target.avatarUrl || null}
          name={target.name}
        />
        {selected && (
          <div className="absolute inset-0 rounded-full flex items-center justify-center" style={{ backgroundColor: `${COLORS.orange}AA` }}>
            <svg className="w-5.5 h-5.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
      </div>
      <span className="flex-1 text-left text-white text-sm font-medium">
        {target.name}
      </span>
      <div
        className={`w-6 h-6 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
          selected
            ? 'bg-filatom-primary border-filatom-primary'
            : 'border-white/30 bg-transparent'
        }`}
      >
        {selected && (
          <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
    </button>
  );
};

// ── Selected Chip ───────────────────────────────────────────────────────────

const SelectedChip = ({ target, onRemove }) => {
  return (
    <div className="flex flex-col items-center mr-3">
      <div className="relative">
        <UserAvatar size={42} imageUrl={target.avatarUrl || null} name={target.name} />
        <button
          onClick={onRemove}
          className="absolute -right-1 -bottom-1 w-4 h-4 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#FF1744' }}
        >
          <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <span className="text-white text-[11px] truncate max-w-[48px] text-center mt-1">
        {target.name.split(' ')[0]}
      </span>
    </div>
  );
};

// ── ForwardContactSelector ──────────────────────────────────────────────────

export const ForwardContactSelector = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const messages = location.state?.messages || [];

  const [allTargets, setAllTargets] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedUids, setSelectedUids] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const currentUserId = user?.uid || '';

  // ── Load targets ──────────────────────────────────────────────────────────

  useEffect(() => {
    const loadTargets = async () => {
      setIsLoading(true);
      try {
        const snap = await getDocs(collection(db, 'users'));
        const targets = snap.docs
          .filter(d => d.id !== currentUserId)
          .map(d => {
            const data = d.data();
            const name = data.name || data.displayName || data.username || 'Unknown';
            const avatar = data.profileImage || data.photoURL || null;
            return new ForwardTarget({ uid: d.id, name, avatarUrl: avatar });
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        setAllTargets(targets);
        setFiltered(targets);
      } catch (error) {
        console.error('Load targets error:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadTargets();
  }, [currentUserId]);

  // ── Search filter ─────────────────────────────────────────────────────────

  useEffect(() => {
    const q = searchQuery.toLowerCase();
    if (!q) {
      setFiltered(allTargets);
      return;
    }
    const filteredList = allTargets.filter(t => 
      t.name.toLowerCase().includes(q)
    );
    setFiltered(filteredList);
  }, [searchQuery, allTargets]);

  // ── Toggle selection ─────────────────────────────────────────────────────

  const toggleSelection = (uid) => {
    const newSet = new Set(selectedUids);
    if (newSet.has(uid)) {
      newSet.delete(uid);
    } else {
      newSet.add(uid);
    }
    setSelectedUids(newSet);
  };

  // ── Send forward ──────────────────────────────────────────────────────────

  const handleSendForward = async () => {
    if (selectedUids.size === 0 || isSending) return;

    setIsSending(true);

    try {
      const actionService = messageActionsService;

      for (const uid of selectedUids) {
        const convId = [currentUserId, uid].sort().join('-');
        await actionService.forwardMessages({
          messages: messages,
          targetConversationId: convId,
          targetReceiverId: uid,
        });
      }

      // Success - go back with result
      navigate(-1);
    } catch (error) {
      console.error('Forward error:', error);
      alert('Failed to forward messages');
    } finally {
      setIsSending(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const selectedTargets = allTargets.filter(t => selectedUids.has(t.uid));

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* Header */}
      <div className="pt-safe-top px-1 pb-2 bg-filatom-bg">
        <div className="flex items-center px-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="flex-1 text-white text-lg font-bold">Forward to</span>
          {selectedUids.size > 0 && (
            <span className="text-filatom-primary text-sm font-semibold">
              {selectedUids.size} selected
            </span>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="px-4 py-2">
        <div
          className="h-11 rounded-full overflow-hidden flex items-center px-4"
          style={{ backgroundColor: COLORS.card }}
        >
          <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search contacts"
            className="flex-1 bg-transparent text-white text-sm outline-none px-3 placeholder:text-gray-400"
          />
        </div>
      </div>

      {/* Selected Chips Row */}
      {selectedUids.size > 0 && (
        <div className="h-[88px] overflow-x-auto px-4 py-2">
          <div className="flex items-center">
            {selectedTargets.map((target) => (
              <SelectedChip
                key={target.uid}
                target={target}
                onRemove={() => toggleSelection(target.uid)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Contact List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <span className="text-gray-400 text-sm">No contacts found</span>
          </div>
        ) : (
          filtered.map((target) => (
            <ContactTile
              key={target.uid}
              target={target}
              selected={selectedUids.has(target.uid)}
              onTap={() => toggleSelection(target.uid)}
            />
          ))
        )}
      </div>

      {/* Send FAB */}
      {selectedUids.size > 0 && (
        <button
          onClick={handleSendForward}
          disabled={isSending}
          className="absolute bottom-6 right-6 px-5 py-3 rounded-full flex items-center gap-2 shadow-lg transition-all hover:opacity-90 disabled:opacity-60"
          style={{ backgroundColor: COLORS.orange }}
        >
          {isSending ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
          <span className="text-white font-bold text-sm">
            {isSending ? 'Sending…' : `Send${selectedUids.size > 1 ? ` (${selectedUids.size})` : ''}`}
          </span>
        </button>
      )}
    </div>
  );
};

export default ForwardContactSelector;